/**
 * Unified Login Page - Sprint 1: Unified Identity & Login Architecture
 * ====================================================================
 * 
 * Single entry point for ALL TreatX platform users:
 * - Patients
 * - Clinic Admins
 * - Sales Reps
 * - Doctors
 * - Clinic Representatives
 * - Coordinators
 * - TreatX Admins
 * 
 * Features:
 * - Automatic role detection on login
 * - Deep link redirect preservation
 * - Invitation-based onboarding support
 * - Clean, professional medical aesthetic
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../utils/api';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useRBAC } from '../context/RBACContext';
import { useClinicData } from '../context/ClinicDataContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2,
  Building2, Shield, Users, Stethoscope, UserCheck, 
  AlertCircle, CheckCircle2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import AppleSSOButton from '../components/AppleSSOButton';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
import { submitPendingQuoteRequest } from '../components/QuoteRequestModal';
import { submitPendingTreatmentIntent } from '../services/TreatmentIntentService';
import { mergeConsentOnLogin } from '../services/CookieConsentService';
import { useContent } from '../hooks/useContent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Role display configuration
// OF-001D #3 — Canonical labels come from the shared resolver
// (`roleLabel(role_id)`).  Icons/colors/descriptions remain local to
// this surface; the LABEL itself must always match the resolver.
import { roleLabel as canonicalRoleLabel } from '../utils/canonicalRoleLabels';
import {
  resolveUnambiguousWorkspace,
  readTrustedWorkspaceSelection,
  persistTrustedWorkspaceSelection,
} from '../utils/workspaceSelection';
const roleConfig = {
  patient: {
    label: canonicalRoleLabel('patient'),
    icon: Heart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    description: 'Access your medical tourism journey'
  },
  provider_admin: {
    label: canonicalRoleLabel('provider_admin'),
    icon: Building2,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: 'Manage your clinic operations'
  },
  provider_sales_rep: {
    label: canonicalRoleLabel('provider_sales_rep'),
    icon: Users,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    description: 'Manage leads and send offers'
  },
  provider_doctor: {
    label: canonicalRoleLabel('provider_doctor'),
    icon: Stethoscope,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'View patients and medical records'
  },
  provider_after_sales_rep: {
    label: canonicalRoleLabel('provider_after_sales_rep'),
    icon: UserCheck,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    description: 'Post-sale patient coordination & aftercare'
  },
  provider_coordinator: {
    label: canonicalRoleLabel('provider_coordinator'),
    icon: UserCheck,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    description: 'Team oversight and lead assignment'
  },
  provider_accountant: {
    label: canonicalRoleLabel('provider_accountant'),
    icon: Building2,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Finance, payments, reporting'
  },
  treatx_super_admin: {
    label: canonicalRoleLabel('treatx_super_admin'),
    icon: Shield,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    description: 'Platform super-administration'
  },
  treatx_admin: {
    label: canonicalRoleLabel('treatx_admin'),
    icon: Shield,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Platform administration'
  }
};

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const { hydrateFromLogin } = useRBAC();
  const { hydrateClinicUser } = useClinicData();
  
  // FIX P0-A: Defensive confetti cleanup — kill any leaked particles from payment success pages
  React.useEffect(() => {
    try {
      const confetti = require('canvas-confetti');
      if (confetti && confetti.reset) confetti.reset();
    } catch (_) { /* ignore */ }
  }, []);

  // TREATX-AUTH-PROD-UAT-FIX-01 (Defect 2) — if a full authenticated
  // session already exists in the AuthContext when the Login route
  // is mounted (e.g. after a password reset auto-signs the user in,
  // or the user manually navigates to /login while authenticated),
  // do NOT render the Login form. Redirect to the canonical default
  // route for the current role/workspace and never leave the user
  // in the hybrid "Login page visible AND authenticated navbar
  // visible" state Owner observed in production UAT.
  React.useEffect(() => {
    if (!user) return;
    // MFA intermediate states never carry a role — they redirect
    // themselves elsewhere via the login handler. Only bail out for
    // a full session that has a resolved role.
    const hasFullSession = !!(user.role || user.roles?.length);
    if (!hasFullSession) return;
    let dest = '/';
    try {
      const stored = JSON.parse(localStorage.getItem('treatx_unified_user') || '{}');
      dest = stored.redirect_path || dest;
    } catch { /* ignore */ }
    // Canonical role → landing mapping mirrors backend
    // `_get_redirect_for_rbac_role`. When a redirect_path was already
    // persisted at login time, respect it; otherwise fall through
    // to the workspace default.
    window.location.hash = dest;
  }, [user]);
  
  // UAT-LOGIN-CLEAN-START-01 — one-shot "you just signed out" hint, read
  // ONCE on mount and immediately consumed so a later visit (or a password
  // manager user returning deliberately) behaves completely normally.
  //
  // What this can and cannot do: the application owns the form state and
  // starts it empty; browser-saved credentials belong to the browser and
  // are never read, deleted or manipulated here. Chrome and other browsers
  // may still re-fill a saved login, and `autocomplete="off"` is widely
  // ignored on credential fields — so this makes the clean start reliable
  // only where the browser honours it, and it must not be described as a
  // universal suppression of autofill.
  const [signedOutStart] = useState(() => {
    try {
      if (typeof sessionStorage === 'undefined') return false;
      const flag = sessionStorage.getItem('treatx_signed_out') === '1';
      if (flag) sessionStorage.removeItem('treatx_signed_out');
      return flag;
    } catch { return false; }
  });
  // Released on the first deliberate interaction, which is what lets the
  // user pick a saved account from the browser's own account chooser.
  const [autofillGuard, setAutofillGuard] = useState(signedOutStart);
  const releaseAutofillGuard = () => setAutofillGuard(false);

  // Form state — pre-fill email from location state (e.g. post-approval
  // redirect), but never carry an address into a form the user just signed
  // out of.
  const [email, setEmail] = useState(
    signedOutStart ? '' : (location.state?.email || ''),
  );
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvalMessage] = useState(location.state?.message || '');
  
  // Role detection state
  const [detectedRole, setDetectedRole] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  // MTMR-P0-CLOSURE/BATCH-A — RESTRICTED context-selection state. The
  // server authenticated the account but granted NO workspace authority
  // because the intended workspace is ambiguous. Until the user picks one
  // (server-validated), no Provider surface is reachable.
  const [pendingWorkspaces, setPendingWorkspaces] = useState(null);

  // CMS content
  const { content: tosConsentContent } = useContent('login_tos_consent_text');
  const [redirectPath, setRedirectPath] = useState(null);

  // TREATX-SOFT-LAUNCH-SIGNUP-BLOCK-01: When the soft-launch gate is ON
  // and the visitor does not hold an invite-key session, hide all
  // signup CTAs. Self-signup is blocked at the backend too — this is
  // only a UX hint to avoid showing dead-end paths.
  const [signupBlocked, setSignupBlocked] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/soft-launch-gate/status`, { credentials: 'omit' });
        if (!r.ok) { if (mounted) setSignupBlocked(true); return; }
        const data = await r.json();
        const enabled = data?.enabled !== false;
        let hasInviteSession = false;
        try {
          const tok = localStorage.getItem('treatx.softLaunch.sessionToken');
          const exp = localStorage.getItem('treatx.softLaunch.sessionExpiresAt');
          hasInviteSession = !!(tok && exp && new Date(exp).getTime() > Date.now());
        } catch (_) { /* ignore */ }
        if (mounted) setSignupBlocked(enabled && !hasInviteSession);
      } catch (_) {
        if (mounted) setSignupBlocked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);
  
  // Get redirect destination from URL params or session
  const redirectTo = searchParams.get('redirect') || sessionStorage.getItem('redirectAfterLogin');
  
  // Handle redirect after successful login using React Router navigation (no page reload)
  useEffect(() => {
    if (loginSuccess && redirectPath) {
      const timer = setTimeout(() => {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath, { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loginSuccess, redirectPath, navigate]);
  
  // Handle unified login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // PASSWORD-DIAGNOSTIC-REMOVAL-01 — the `?login_debug=1` browser-parity
      // probe that logged the outgoing password LENGTH plus a SHA-256 prefix
      // (with the email and user-agent) has been removed. Those bytes are
      // password-derived material: a length plus a 48-bit digest prefix lets
      // a guessed password be confirmed offline. No password value or
      // derivative may appear in logs, diagnostics or telemetry. The
      // Chrome/Edge parity investigation it was added for is closed.

      // Use axios instead of fetch to avoid "body stream already read" issue with error responses
      let data;
      try {
        const axiosResponse = await axios.post(`${API_URL}/api/auth/unified/login`, { email, password });
        data = axiosResponse.data;
      } catch (axiosErr) {
        if (axiosErr.response) {
          // Server responded with error status
          data = axiosErr.response.data;
          const errorCode = data?.error_code || '';

          // TREATX-P0-ONBOARDING-UNIFICATION-01: server-side auto-resume.
          // If the server detected an unfinished RegistrationSession
          // matching (email, password), redirect the user into the
          // wizard's waiting phase — regardless of browser or device.
          // This is the ONLY canonical recovery path: server is the
          // source of truth, localStorage is optional cache.
          if (errorCode === 'ONBOARDING_IN_PROGRESS' && data?.registration_session) {
            try {
              const rs = data.registration_session;
              const envelope = {
                session_id: rs.session_id,
                session_secret: rs.session_secret,
                email: rs.email,
                role: rs.role,
                expires_at: rs.expires_at,
                email_mode: 'smtp',
                activation_link: null,
              };
              // Persist as cache (opt-in, not authoritative).
              try {
                localStorage.setItem(
                  `treatx.onboarding.registrationSession.${rs.role}`,
                  JSON.stringify(envelope)
                );
              } catch { /* quota / disabled */ }
              // Send the user straight to the wizard.
              // Normalise any of the following server-returned shapes
              // into a bare "/route" that we can safely assign to
              // window.location.hash:
              //   • "/#/provider/onboarding"   (canonical response)
              //   • "#/provider/onboarding"
              //   • "/provider/onboarding"
              //   • "provider/onboarding"
              // The previous naive `resumeUrl.replace(/^#/, '')` left
              // the leading "/#" untouched, producing "#/#/provider/..."
              // which the hash router could not resolve — the user
              // was stranded on the Login page.
              const rawResume = data?.resume_url || `#/${rs.role === 'patient' ? 'patient' : rs.role === 'clinic_admin' ? 'clinic' : 'provider'}/onboarding`;
              let hashPath = String(rawResume).replace(/^\/?#\/?/, '/');
              if (!hashPath.startsWith('/')) hashPath = '/' + hashPath;
              window.location.hash = hashPath;
              // Force a fresh mount so the wizard rehydrates.
              setTimeout(() => window.location.reload(), 50);
              return;
            } catch (_e) {
              /* fall through to normal error handling */
            }
          }

          // O90: the backend no longer distinguishes "unknown email" from
          // "wrong password" (account-enumeration closure), so there is no
          // account-existence branch to render here either.
          // Bug #1 Fix: If backend says "use patient login", automatically try patient endpoint
          if (errorCode === 'USE_PATIENT_LOGIN') {
            try {
              const patientResponse = await axios.post(`${API_URL}/api/auth/patient/login`, { email, password });
              const pData = patientResponse.data;
              // Normalize patient response to match unified login format
              const patient = pData.patient || {};
              data = {
                success: true,
                role: 'patient',
                redirect_path: '/patient/dashboard',
                user: {
                  id: patient.id,
                  email: patient.email,
                  name: patient.name || email.split('@')[0],
                  phone: patient.phone,
                },
                person_id: pData.person_id || patient.id,
                clinic_id: null,
                permissions: [],
                role_assignments: [],
              };
            } catch (patientErr) {
              throw new Error(patientErr?.response?.data?.detail || 'Invalid email or password');
            }
          } else {
            // O90: one generic, localised credential failure — never echo an
            // account-existence hint back to the user.
            throw new Error(t('patient.auth.invalidCredentials', 'Email or password is incorrect'));
          }
        } else {
          throw new Error('Unable to connect. Please try again.');
        }
      }
      
      if (data.success) {
        // TREATX-MFA-PROD-FIX-01 — detect MFA gate BEFORE applying
        // full login result. The backend returns:
        //   { success:true, mfa_required:true,           mfa_token, expires_in_minutes, user:{id,email} }
        //   { success:true, mfa_enrollment_required:true, mfa_token, expires_in_minutes, user:{id,email} }
        // for privileged accounts. In either branch we hold the
        // intermediate token in sessionStorage and route the user to
        // the dedicated MFA page — we MUST NOT hydrate the RBAC
        // context, MUST NOT set any user cookie, MUST NOT emit
        // "Welcome back". The full session is only issued after
        // /api/mfa/challenge/complete or /api/mfa/enroll/confirm.
        if (data.mfa_required || data.mfa_enrollment_required) {
          try {
            sessionStorage.setItem('treatx.mfa.token', data.mfa_token || '');
            sessionStorage.setItem('treatx.mfa.email', data?.user?.email || email);
            sessionStorage.setItem('treatx.mfa.expiresInMinutes',
              String(data.expires_in_minutes || 5));
          } catch { /* sessionStorage disabled — non-fatal */ }
          const target = data.mfa_enrollment_required
            ? '/mfa/enroll'
            : '/mfa/challenge';
          // Hash router — assign the hash directly so we always land
          // on the target route regardless of subrouter context.
          window.location.hash = target;
          return;
        }
        await applyLoginResult(data);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  // TREATX-GOOGLE-OAUTH-FRONTEND-01: map backend error codes to friendly UI strings.
  const friendlyGoogleError = (code, fallback) => {
    switch (code) {
      case 'pending_provider_application':
        return 'A provider application is in progress for this email. Open the verification link in your inbox to continue onboarding, or contact support if you have lost it.';
      case 'mfa_required_for_admin':
        return 'Administrator accounts cannot use Google sign-in yet. Please sign in with your email and password.';
      case 'email_not_verified_by_google':
        return 'Google has not verified ownership of this email yet. Please verify your email with Google first, then try again.';
      case 'google_oauth_disabled':
        return 'Google sign-in is temporarily unavailable. Please sign in with your email and password.';
      case 'invalid_id_token':
        return 'Google sign-in could not be verified. Please try again.';
      case 'account_deactivated':
        return 'This account has been deactivated. Please contact your administrator.';
      default:
        return fallback || 'Google sign-in failed. Please try again or use email and password.';
    }
  };

  // TREATX-GOOGLE-OAUTH-FRONTEND-01: extract a backend error code from any
  // shape we might receive:
  //   1. detail.code      (HTTPException with dict detail, no global handler)
  //   2. error  (string)  (legacy social handler payload)
  //   3. message contains a stringified Python dict — the global
  //      error_handler in /app/backend/middleware/error_handler.py
  //      converts dict details to `str(exc.detail)`, so we recover the
  //      structured `code` from the embedded text via a regex.
  const extractErrorCode = (errResp) => {
    if (!errResp) return null;
    const d = errResp.detail;
    if (d && typeof d === 'object' && d.code) return d.code;
    if (errResp.error && typeof errResp.error === 'string') return errResp.error;
    if (typeof errResp.message === 'string') {
      const m = errResp.message.match(/'code'\s*:\s*'([a-zA-Z0-9_]+)'/);
      if (m && m[1]) return m[1];
      const m2 = errResp.message.match(/"code"\s*:\s*"([a-zA-Z0-9_]+)"/);
      if (m2 && m2[1]) return m2[1];
    }
    return null;
  };

  // Extract a user-readable message embedded inside a stringified Python
  // dict (mirror of extractErrorCode).
  const extractEmbeddedMessage = (errResp) => {
    if (!errResp || typeof errResp.message !== 'string') return null;
    const m = errResp.message.match(/'message'\s*:\s*'([^']+)'/);
    if (m && m[1]) return m[1];
    const m2 = errResp.message.match(/"message"\s*:\s*"([^"]+)"/);
    if (m2 && m2[1]) return m2[1];
    return null;
  };

  // TREATX-GOOGLE-OAUTH-FRONTEND-01: direct Google Identity Services credential handler.
  // Receives the `credential` (id_token) from the GoogleLogin button and exchanges
  // it with the canonical backend endpoint /api/auth/social/google/id-token.
  const handleGoogleCredential = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      setError(friendlyGoogleError('invalid_id_token'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API_URL}/api/auth/social/google/id-token`, {
        id_token: idToken,
      });
      const data = resp.data;
      if (!data?.success) {
        setError(friendlyGoogleError(null, data?.detail || 'Google sign-in failed.'));
        return;
      }
      await applyLoginResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const code = extractErrorCode(err?.response?.data);
      const embedded = extractEmbeddedMessage(err?.response?.data);
      const fallback = embedded
        || ((typeof err?.response?.data?.detail === 'string') ? err.response.data.detail : null);
      if (status === 409 || status === 401 || status === 403 || status === 503) {
        setError(friendlyGoogleError(code, fallback));
      } else {
        setError(friendlyGoogleError(code, fallback || 'Google sign-in failed. Please try again.'));
      }
      console.error('[Google OAuth] login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(friendlyGoogleError(null, 'Google sign-in was cancelled or blocked. Please try again.'));
  };

  // TREATX-APPLE-OAUTH-FRONTEND-01 — Apple identity-token handler.
  // Receives { id_token, name } from the Apple JS SDK popup and exchanges
  // it with the backend endpoint /api/auth/social/apple. Mirrors the
  // Google flow (post-verify → applyLoginResult).
  const handleAppleCredential = async ({ id_token, name }) => {
    if (!id_token) {
      setError('Apple sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API_URL}/api/auth/social/apple`, {
        id_token,
        name: name || null,
      });
      const data = resp.data;
      if (!data?.success) {
        const detail = (typeof data?.detail === 'string') ? data.detail : 'Apple sign-in failed.';
        setError(detail);
        return;
      }
      await applyLoginResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const embedded = extractEmbeddedMessage(err?.response?.data);
      const fallback = embedded
        || ((typeof err?.response?.data?.detail === 'string') ? err.response.data.detail : null);
      if (status === 503) {
        setError('Apple Sign-In is not enabled yet. Please use another sign-in method.');
      } else if (status === 401) {
        setError(fallback || 'Apple could not verify this sign-in. Please try again.');
      } else {
        setError(fallback || 'Apple sign-in failed. Please try again.');
      }
      console.error('[Apple OAuth] login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleError = (err) => {
    if (err?.code === 'apple_not_configured') {
      toast.info('Apple Sign-In is coming soon.');
      return;
    }
    if (err?.code === 'popup_closed' || err?.code === 'popup_closed_by_user') {
      return; // silent — user cancelled
    }
    // TREATX-APPLE-REAL-UAT-MISSING-ID-TOKEN-01 — the redirect flow
    // takes the browser away from this page before this handler fires,
    // so any error surfaced HERE is an SDK-init / config failure, not
    // a cancellation. Do not mislead the user with "cancelled".
    setError(
      err?.code
        ? `Apple sign-in could not start (${err.code}). Please try again or use another method.`
        : 'Apple sign-in could not start. Please try again or use another method.'
    );
  };

  // Legacy no-op preserved for the old prop path used by the disabled
  // button; kept to avoid regressing any callers that still reference it.
  const handleAppleLogin = () => {
    toast.info('Apple Sign-In is coming soon.');
  };

  // Shared post-login handler used by BOTH email/password and Google flows.
  // Persists canonical auth state, hydrates RBAC + clinic contexts, and
  // computes the deterministic landing route. Mirrors the previous inline
  // success branch from handleLogin().
  const applyLoginResult = async (data) => {
    // SECURITY-S4: storage truth collapse — purge legacy mirrors first.
        localStorage.removeItem('treatx_unified_user');
        localStorage.removeItem('treatx_user');
        localStorage.removeItem('treatx_rbac_permissions');
        localStorage.removeItem('treatx_rbac_role_assignments');
        localStorage.removeItem('treatx_admin');
        localStorage.removeItem('treatx_coordinator_token');
        localStorage.removeItem('treatx_coordinator_user');
        localStorage.removeItem('clinicRepresentative');
        sessionStorage.removeItem('treatx_active_clinic_id');
        sessionStorage.removeItem('treatx_clinic_user');

        // Store canonical token + user only.
        localStorage.setItem('treatx_unified_user', JSON.stringify(data.user));

        // Store RBAC permissions and role_assignments for RBACContext
        localStorage.setItem('treatx_rbac_permissions', JSON.stringify(data.permissions || []));
        localStorage.setItem('treatx_rbac_role_assignments', JSON.stringify(data.role_assignments || []));
        localStorage.setItem('treatx_rbac_workspaces', JSON.stringify(data.workspaces || []));

        // Hydrate RBAC context immediately (no page reload needed)
        hydrateFromLogin(data.permissions || [], data.role_assignments || [], data.workspaces || [], {
          activeContext: data.active_context || null,
          requiresContextSelection: Boolean(data.requires_context_selection),
        });

        try { window.dispatchEvent(new Event('treatx:identity-changed')); } catch { /* ignore */ }
        
        // Also store in role-specific storage for backward compatibility
        storeRoleSpecificAuth(data);
        
        if (['provider_admin', 'provider_sales_rep', 'provider_doctor', 'provider_after_sales_rep'].includes(data.role)) {
          hydrateClinicUser({
            ...data.user,
            clinicId: data.clinic_id,
            clinicName: data.user.clinic_name || data.clinic_name || '',
            role: data.role,
            ...(data.doctor_id ? { doctorId: data.doctor_id } : {}),
          });
        }
        
        // Show success state with role detection
        setDetectedRole(data.role);
        setLoginSuccess(true);
        
        toast.success(`Welcome back, ${data.user.name}!`);
        
        let destination = redirectTo || null;
        const wss = (data.workspaces || []).filter(w => w.is_available && w.default_route);
        // TREATX-UAT-PROD-CONTEXT-SURFACE-01c — ONE shared automatic
        // selection rule (see `utils/workspaceSelection`). A one-entry
        // catalogue is NOT a proof of a single persona.
        const unambiguousWs = resolveUnambiguousWorkspace(
          data.role_assignments || [], data.workspaces || []
        );
        if (!destination) {
          if (unambiguousWs) {
            destination = unambiguousWs.default_route;
            persistTrustedWorkspaceSelection(unambiguousWs.workspace_id);
          } else if (wss.length >= 1) {
            // BATCH-A: only an EXPLICIT previous selection may be restored.
            // No patient/first-workspace inference.
            const lastId = readTrustedWorkspaceSelection();
            const lastWs = lastId ? wss.find(w => w.workspace_id === lastId) : null;
            destination = lastWs ? lastWs.default_route : null;
          } else {
            destination = data.redirect_path;
          }
        }

        // MTMR-P0-CLOSURE/BATCH-A — the server issues NO Provider
        // authority for a multi-workspace account until a workspace is
        // selected (RESTRICTED context-selection session).
        //
        // Intent is NEVER inferred from assignment order. The client may
        // only:
        //   (a) restore a workspace the user previously selected
        //       EXPLICITLY (`treatx_last_active_workspace`), or
        //   (b) select the single available workspace (unambiguous).
        // Anything else renders the explicit chooser — no navigation into
        // a Provider surface. Every proposal is validated server-side
        // against `rbac_users.role_assignments` before `act` is minted.
        if (data.requires_context_selection) {
          const _lastId = readTrustedWorkspaceSelection();
          const _restored = _lastId ? wss.find(w => w.workspace_id === _lastId) : null;
          const _chosen = _restored || unambiguousWs;
          if (!_chosen) {
            // AMBIGUOUS — the user must choose. Stay put and show the
            // canonical selection surface.
            setPendingWorkspaces(wss);
            setLoading(false);
            setLoginSuccess(false);
            toast.info('Select a workspace to continue.');
            return;
          }
          try {
            const ctxData = await applyWorkspaceSelection(
              _chosen.workspace_id, data.role_assignments || [], data.workspaces || []
            );
            destination = ctxData.default_route || destination;
          } catch (ctxErr) {
            console.warn('[MTMR-P0] initial context selection refused', ctxErr?.message);
            setPendingWorkspaces(wss);
            setLoading(false);
            setLoginSuccess(false);
            toast.error('Please select a workspace to continue.');
            return;
          }
        }
        
        const userData = {
          id: data.user.id,
          person_id: data.person_id || data.user.id,
          email: data.user.email,
          name: data.user.name,
          role: data.role,
          phone: data.user.phone,
        };

        login(userData);
        mergeConsentOnLogin();
        
        // TREATX-ADMIN-ONBOARDING-01 · frontend gate.
        // Compute onboarding-diverted destination BEFORE the delayed
        // navigation fires so we never briefly land on the workspace
        // route (last-write-wins would otherwise overwrite our
        // redirect). Server-side is authoritative regardless.
        let onboardingRedirect = null;
        // TREATX-BATCH-BOOT-01 (residual sweep) — the
        // /api/admin/onboarding/status probe MUST NOT fire for
        // non-admin logins. Patient / clinic-admin sessions produce
        // 403 noise here and inflate anonymous-adjacent 4xx rate.
        // Gate on the just-authenticated user's role instead of
        // swallowing the 403 after the fact.
        const _u_role = String(userData?.role || '').toLowerCase();
        const _ADMIN_ROLES = ['admin', 'treatx_admin', 'super_admin', 'platform_admin'];
        if (_ADMIN_ROLES.includes(_u_role)) {
          try {
            const statusRes = await axios.get(
              `${API_URL}/api/admin/onboarding/status`,
              { withCredentials: true },
            );
            const st = statusRes?.data || {};
            if (st.must_change_password) {
              onboardingRedirect = '/admin/onboarding/change-password';
            } else if (st.email_verified === false) {
              onboardingRedirect = '/admin/onboarding/verify-required';
            }
          } catch { /* admin caller 401/403 here — leave normal path */ }
        }

        setTimeout(async () => {
          sessionStorage.removeItem('redirectAfterLogin');
          if (onboardingRedirect) {
            window.location.hash = onboardingRedirect;
            return;
          }
          try {
            const pendingResult = await submitPendingQuoteRequest();
            if (pendingResult?.success) {
              toast.success(`Quote request sent to ${pendingResult.clinic_name || 'the clinic'}!`);
              window.location.hash = '/patient/dashboard';
              return;
            }
          } catch (err) {
            console.error('Failed to submit pending quote:', err);
          }
          try {
            const intentResult = await submitPendingTreatmentIntent();
            if (intentResult?.success) {
              const msg = intentResult.deduplicated
                ? `You already have a request for ${intentResult.treatment || 'this treatment'}.`
                : `Request created for ${intentResult.treatment || 'your selected treatment'}!`;
              toast.success(msg);
              window.location.hash = '/patient/dashboard';
              return;
            }
          } catch (err) {
            console.error('Failed to submit treatment intent:', err);
          }
          window.location.hash = destination;
        }, 500);
        
        setRedirectPath(onboardingRedirect || destination);
  };
  
  // SECURITY-S4: storeRoleSpecificAuth collapsed.
  // The only role-scoped sessionStorage keys that are still written are
  // the clinic context (`treatx_clinic_user` + `treatx_active_clinic_id`)
  // because ClinicDataContext / useClinicScope consume them as active
  // workspace context, NOT as an independent auth-truth.
  // No more legacy mirrors (treatx_patient_token, treatx_coordinator_*,
  // clinicRepresentative, treatx_admin) are written here.
  const storeRoleSpecificAuth = (data) => {
    const { user, role, clinic_id } = data;
    if (['provider_admin', 'provider_coordinator', 'provider_sales_rep', 'provider_doctor', 'provider_after_sales_rep'].includes(role)) {
      sessionStorage.setItem('treatx_clinic_user', JSON.stringify({
        ...user,
        clinicId: clinic_id,
        clinicName: user.clinic_name || data.clinic_name || '',
        role: role,
      }));
      if (clinic_id) {
        sessionStorage.setItem('treatx_active_clinic_id', clinic_id);
      }
    }
  };

  // Google login via direct Google Identity Services — handled by
  // handleGoogleCredential() above. The old Emergent OAuth proxy redirect
  // flow has been excised (TREATX-GOOGLE-OAUTH-FRONTEND-01).
  
  // MTMR-P0-CLOSURE/BATCH-A — the ONLY client path out of the restricted
  // context-selection state: propose a workspace, let the SERVER validate
  // it against `rbac_users.role_assignments` and re-mint `act`.
  const applyWorkspaceSelection = async (workspaceId, roleAssignments, allWorkspaces) => {
    const ctxRes = await api.post('/auth/unified/context', { workspace_id: workspaceId });
    const ctxData = ctxRes?.data || {};
    hydrateFromLogin(
      ctxData.permissions || [], roleAssignments || [], allWorkspaces || [],
      { activeContext: ctxData.active_context || null, requiresContextSelection: false },
    );
    // Explicit user selection, already validated by the server above.
    persistTrustedWorkspaceSelection(workspaceId);
    try {
      localStorage.setItem('treatx_rbac_permissions', JSON.stringify(ctxData.permissions || []));
    } catch { /* ignore */ }
    return ctxData;
  };

  if (pendingWorkspaces) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4"
        data-testid="context-selection-required"
      >
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-1">Select a workspace</h2>
            <p className="text-sm text-slate-500 mb-6">
              Your account can act in more than one workspace. Choose the one you
              want to work in — each workspace is kept completely separate.
            </p>
            <div className="space-y-2">
              {pendingWorkspaces.map((ws) => (
                <button
                  key={ws.workspace_id}
                  type="button"
                  data-testid={`context-selection-option-${ws.workspace_id}`}
                  className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors"
                  onClick={async () => {
                    try {
                      const ctxData = await applyWorkspaceSelection(
                        ws.workspace_id,
                        JSON.parse(localStorage.getItem('treatx_rbac_role_assignments') || '[]'),
                        pendingWorkspaces,
                      );
                      setPendingWorkspaces(null);
                      navigate(ctxData.default_route || ws.default_route);
                    } catch (err) {
                      toast.error('That workspace is no longer available.');
                    }
                  }}
                >
                  <div className="text-sm font-semibold text-slate-800">{ws.display_name || ws.label || ws.workspace_id}</div>
                  <div className="text-xs text-slate-500">{ws.clinic_name || ws.type}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state with role animation
  if (loginSuccess && detectedRole) {
    const config = roleConfig[detectedRole] || roleConfig.patient;
    const Icon = config.icon;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
        <Card className="w-full max-w-md animate-in zoom-in-95 duration-300">
          <CardContent className="p-8 text-center">
            <div className={`w-20 h-20 ${config.bgColor} rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse`}>
              <Icon className={`h-10 w-10 ${config.color}`} />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <h2 className="text-2xl font-bold text-slate-800">Login Successful</h2>
            </div>
            <Badge className={`${config.bgColor} ${config.color} border-0 mb-4`}>
              {config.label}
            </Badge>
            <p className="text-slate-600 mb-4">{config.description}</p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Redirecting to your dashboard...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-slate-50 via-white to-primary/5 px-4 py-8" data-testid="unified-login-page">
      {/* Sign In column — compact, premium */}
      <div className="w-full max-w-[460px]">
        {/* Logo & Header */}
        <div className="text-center mb-4">
          <div className="w-11 h-11 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
            <Heart className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-0.5">{t('patient.auth.signInTitle')}</h1>
          <p className="text-sm text-slate-500">{t('patient.auth.signInSubtitle')}</p>
        </div>
        
        {/* Redirect Notice */}
        {redirectTo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <p className="text-xs text-blue-700">Sign in to continue to your destination</p>
          </div>
        )}

        {/* Approval Message (from clinic approval redirect) — hide when an
            error is present so we never show the confusing "approved" + 
            "Account deactivated" combo (Phase 3). */}
        {approvalMessage && !error && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 mb-4 flex items-center gap-2" data-testid="approval-message-banner">
            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
            <p className="text-xs text-green-700">{approvalMessage}</p>
          </div>
        )}
        
        {/* Login Card */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-1 pt-4 px-5">
            <CardTitle className="text-base">{t('patient.auth.signIn')}</CardTitle>
            <CardDescription className="text-xs">
              {t('patient.auth.signInHint')}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="px-5 pb-5 pt-1">
            {/* Social Login Buttons (above form for prominence) */}
            <div className="space-y-2 mb-3">
              {/* TREATX-GOOGLE-OAUTH-FRONTEND-01: official Google Identity
                  Services button. Returns a signed id_token credential
                  which is verified server-side at
                  POST /api/auth/social/google/id-token. */}
              {/* TREATX-GOOGLE-GIS-LOAD-ON-SURFACE-01 — the provider (and
                  therefore Google Identity Services) is mounted here, on the
                  Google sign-in surface, instead of globally in App.js. One
                  Tap and automatic sign-in stay disabled. */}
              <div className="w-full flex items-center justify-center" data-testid="google-login-btn">
                {GOOGLE_CLIENT_ID && (
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLogin
                  onSuccess={handleGoogleCredential}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  width="380"
                  text="signin_with"
                  shape="rectangular"
                  logo_alignment="left"
                />
                </GoogleOAuthProvider>
                )}
              </div>
              
              <AppleSSOButton
                onSuccess={handleAppleCredential}
                onError={handleAppleError}
                loading={loading}
                testId="apple-login-btn"
                size="small"
              />
            </div>
            
            <div className="mb-3 flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">{t('patient.auth.orEmail')}</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-3" autoComplete={autofillGuard ? 'off' : 'on'}>
              {signedOutStart && autofillGuard && !error && (
                <div
                  className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600"
                  role="status"
                  data-testid="signed-out-notice"
                >
                  {t('patient.auth.signedOutNotice')}
                </div>
              )}
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3" data-testid="login-error-message">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-700" data-testid="login-error-text">{error}</p>
                  </div>
                </div>
              )}
              
              {/* Email Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="unified-login-email">{t('patient.auth.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="unified-login-email"
                    name="email"
                    type="email"
                    autoComplete={autofillGuard ? 'off' : 'username'}
                    readOnly={autofillGuard}
                    onFocus={releaseAutofillGuard}
                    onPointerDown={releaseAutofillGuard}
                    inputMode="email"
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="pl-10 h-10"
                    disabled={loading}
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="unified-login-password">{t('patient.auth.password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="unified-login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={autofillGuard ? 'off' : 'current-password'}
                    readOnly={autofillGuard}
                    onFocus={releaseAutofillGuard}
                    onPointerDown={releaseAutofillGuard}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    // PASSWORD-FIELD-SEMANTICS-01 — the placeholder used to be
                    // "••••••••", which is visually IDENTICAL to a populated
                    // masked password field. An EMPTY field therefore looked
                    // like it still held the previous user's password after
                    // logout. No placeholder here means empty reads as empty.
                    // (No i18n key needed, so EN/TR parity is unaffected.)
                    className="pl-10 pr-10 h-10"
                    disabled={loading}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600">
                  <input type="checkbox" className="rounded border-slate-300" />
                  {t('patient.auth.rememberMe')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('patient.auth.forgotPassword')}
                </button>
              </div>
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-primary hover:bg-primary/90 text-white rounded-full font-medium text-sm"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('patient.auth.signingIn')}
                  </>
                ) : (
                  <>
                    {t('patient.auth.signIn')}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            {/* Forgot password helper — keep visible for returning users */}
            <p className="text-center text-xs text-slate-400 mt-4">
              {t('patient.auth.troubleSigningIn')}{' '}
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-primary hover:underline font-medium"
                data-testid="reset-password-link"
              >
                {t('patient.auth.resetPassword')}
              </button>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ─── New to TreatX? onboarding section ─────────────────────────
          TREATX-LOGIN-IDENTITY-CLARITY-01:
          Aligns the onboarding entry surface with the canonical TreatX
          identity model. Four cards represent the four user entry
          paths (Patient / Doctor / Provider / Team Member). Patient
          remains the visually dominant CTA — patient acquisition is
          the primary business goal. Doctor signup visibility is
          restored. Staff (Coordinator / Sales / Accounting / After
          Sales) are guided to the same account-creation flow as
          patients: TreatX does not maintain a separate staff identity
          model.

          TREATX-SOFT-LAUNCH-SIGNUP-BLOCK-01: While the soft-launch
          gate is enabled AND the visitor does not hold an invite-key
          session, the entire onboarding section (cards + hidden legacy
          links) is suppressed so anonymous users have no signup
          entry point. The backend mirrors this with a 503 deny on
          /api/auth signup endpoints (see soft_launch_gate_middleware). */}
      {!signupBlocked ? (
        <div className="w-full max-w-5xl mt-8 px-2" data-testid="onboarding-section">
        <div className="text-center mb-5">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-1">New to TreatX?</h2>
          <p className="text-sm text-slate-500">Choose how you would like to join.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* ─── Card 1 — Patient (PRIMARY) ─── */}
          <Link
            to="/patient/register"
            data-testid="onboarding-patient-card"
            className="group relative flex flex-col h-full rounded-2xl border-2 border-primary/50 bg-gradient-to-br from-primary/8 via-primary/5 to-white hover:border-primary hover:shadow-lg transition-all p-5 overflow-hidden md:col-span-2"
          >
            <div className="absolute top-3 right-3">
              <Badge className="bg-primary/15 text-primary border-0 text-[10px] font-semibold uppercase tracking-wider">Most Popular</Badge>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Heart className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary/70 mb-1">Patient</p>
                <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">Looking for treatment or medical services?</h3>
                <p className="text-xs sm:text-sm text-slate-600">Browse vetted hospitals, clinics and doctors. Get quotes, book consultations and manage everything in one place.</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-slate-500">Free — takes under a minute</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                Create Patient Account
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>

          {/* ─── Card 2 — Healthcare Professional (Doctor) ─── */}
          <Link
            to="/doctor/signup"
            data-testid="onboarding-doctor-card"
            className="group relative flex flex-col h-full rounded-2xl border border-teal-300/70 bg-white hover:border-teal-500 hover:shadow-md hover:bg-teal-50/40 transition-all p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <Stethoscope className="h-5 w-5 text-teal-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-teal-700/80 mb-1">Healthcare Professional</p>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-1">Create your doctor profile</h3>
                <p className="text-xs text-slate-600 leading-relaxed">Doctors create and manage their professional profile here. You may later join one or more healthcare organizations or create your own Doctor Practice.</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 group-hover:gap-2 transition-all">
                Create Doctor Profile
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>

          {/* ─── Card 3 — Healthcare Organization (Provider) ─── */}
          <Link
            to="/provider/register"
            data-testid="onboarding-provider-card"
            className="group relative flex flex-col h-full rounded-2xl border border-emerald-300/70 bg-white hover:border-emerald-500 hover:shadow-md hover:bg-emerald-50/40 transition-all p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/80 mb-1">Healthcare Organization</p>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-1">Register your organization</h3>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">For the legal entity that receives bookings, contracts and payments.</p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Hospital</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Clinic</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Agency</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Doctor Practice</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:gap-2 transition-all">
                Register Organization
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>

          {/* ─── Card 4 — Joining an Existing Organization (Staff) ─── */}
          <Link
            to="/patient/register"
            data-testid="onboarding-staff-card"
            className="group relative flex flex-col h-full rounded-2xl border border-slate-200 bg-slate-50/60 hover:border-slate-400 hover:shadow-md hover:bg-white transition-all p-5 md:col-span-2"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-5 w-5 text-slate-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Joining an Existing Organization?</p>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-1">Coordinators, Sales, After-Sales, Accounting & team members</h3>
                <p className="text-xs text-slate-600 leading-relaxed">Create an account first, then share your email with your organization administrator to receive an invitation. No separate staff signup — your TreatX account is everything you need.</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 group-hover:gap-2 transition-all">
                Create Account
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </div>

        {/* Hidden legacy testids preserved for prior automation / external linkers. */}
        <Link to="/patient/register" data-testid="create-account-link" className="sr-only">Create patient account</Link>
        <Link to="/provider/register" data-testid="provider-register-link" className="sr-only">Register as a provider</Link>
        <Link to="/doctor/signup" data-testid="doctor-signup-link" className="sr-only">Create doctor profile</Link>
      </div>
      ) : (
        <div
          className="w-full max-w-5xl mt-8 px-2"
          data-testid="onboarding-section-blocked"
        >
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-5 py-4 text-center">
            <p className="text-sm font-semibold text-slate-700">
              {t('patient.auth.softLaunchNotice').split('.')[0] + '.'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {t('patient.auth.softLaunchNotice').split('.').slice(1).join('.').trim()}{' '}
              <a
                href="mailto:support@treatx.health"
                className="text-primary font-semibold hover:underline"
                data-testid="support-email-link"
              >
                support@treatx.health
              </a>.
            </p>
          </div>
        </div>
      )}

      <div className="w-full max-w-[460px]">
        {/* Footer */}
        <p className="text-center text-[10px] text-slate-400 mt-6">
          {tosConsentContent?.body || 'By signing in or creating an account, you agree to our'}{' '}
          <Link to="/terms" className="underline hover:text-slate-600">Terms of Service</Link>
          {' & '}
          <Link to="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
